import i18n from '@/i18n'
import axios from 'axios'

const BLOCKED_KEYS = ['__proto__', 'constructor', 'prototype']

const sanitize = (obj) => {
  if (!obj || typeof obj !== 'object') return obj

  if (obj instanceof FormData) return obj
  if (obj instanceof URLSearchParams) return obj

  if (Array.isArray(obj)) {
    return obj.map(sanitize)
  }

  const clean = {}

  for (const key in obj) {
    if (BLOCKED_KEYS.includes(key)) continue

    const value = obj[key]
    clean[key] = sanitize(value)
  }

  return clean
}

const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '')
const apiBaseUrl = import.meta.env.DEV
  ? '/v1/asp/'
  : configuredApiBaseUrl
    ? `${configuredApiBaseUrl}/v1/asp/`
    : '/v1/asp/'

const axiosInstance = axios.create({
  timeout: 10000,
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // 필요한 경우
})

// 요청 인터셉터
axiosInstance.interceptors.request.use((config) => {
  if (config.data) {
    config.data = sanitize(config.data)
  }
  if (config.params) {
    config.params = sanitize(config.params)
  }
  return config
})

// 응답 인터셉터
axiosInstance.interceptors.response.use(
  (res) => {
    return res
  },

  (err) => {
    const isQAMode = import.meta.env.VITE_QA_MODE === 'true'
    const code = err.response?.status
    const status = err?.status
    const errCode = err.response?.data?.rspCode
    const url = err.config?.url
    const isTestRoute = location.pathname === '/test-auth'
    const isPublicRequest =
      url === '/org/info' ||
      url === '/system/server-time' ||
      err.config?.headers?.['X-Allow-Anonymous']
    const isAllowableA1004 = errCode === 'A1004' && isQAMode && (isTestRoute || isPublicRequest)

    if (isAllowableA1004 || isTestRoute) {
      // qa모드에서 토큰 없는 진입 초기 상황에서 발생한 A1004는 무시
      console.warn('[초기 진입 허용된 A1004]', code, errCode)
      return Promise.reject(err)
    }

    // 시스템 에러 코드 (A1004)
    if (errCode === 'A1004') {
      console.error('[종료 요청됨]', err.response?.data?.rspMessage)
      const error = new Error(err.response?.data?.rspMessage || i18n.t('error.axios.system'))
      error.status = 'SYSTEM'
      error.response = err.response
      return Promise.reject(error)
    }

    // Timeout 에러
    if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      const error = new Error(i18n.t('error.axios.timeout'))
      error.description = i18n.t('error.axios.tryAgainLater')
      error.action = 'retry'
      return Promise.reject(error)
    }

    // 네트워크 에러
    if (!err.response && err.message === 'Network Error') {
      const error = new Error(i18n.t('error.axios.networkConnection'))
      error.description = i18n.t('error.axios.networkDisconnected')
      error.code = 'NETWORK'
      error.action = 'retry'
      error.url = err?.config?.url
      return Promise.reject(error)
    }

    if (status === 400) {
      const error = new Error(i18n.t('error.axios.badRequest'))
      error.action = 'home'
      return Promise.reject(error)
    }

    if (code === 401) {
      return Promise.reject(new Error(i18n.t('error.axios.unauthorized')))
    }

    if (code === 403) {
      return Promise.reject(new Error(i18n.t('error.axios.forbidden')))
    }

    if (code > 400 && code < 500) {
      return Promise.reject(new Error(i18n.t('error.axios.requestFailed')))
    }

    // 서버 500+ 에러
    if (code >= 500) {
      const error = new Error(i18n.t('error.axios.server'))
      error.action = 'retry'
      return Promise.reject(error)
    }

    return Promise.reject(err)
  }
)

export default axiosInstance
